// Book a photographer — the third door.
//
// The quote form, its API and its admin inbox already existed at /photographer,
// styled like the booking app and reachable from nowhere. This puts a real page
// in front of the same endpoint: what we shoot, how it works, the form. The
// field names and option values are the ones /api/photographer-quote and the
// admin inbox already read, so nothing server-side changes.
//
// No prices. Sessions are quoted on what someone actually needs, and the form
// asks for a budget so the quote can be shaped to it.

import React, { useState } from 'react';
import { Bay, Btn, Card, Eyebrow, Head, OtherDoor, PhotoStrip } from '../primitives.jsx';
import { useSiteData, workPhotos } from '../data.js';
import { go } from '../routes.js';
import { PHOTOGRAPHY, STUDIO } from '../content.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

const TIMELINES = [
  ['asap', 'ASAP / this week'],
  ['2-4-weeks', 'In the next 2–4 weeks'],
  ['1-2-months', '1–2 months out'],
  ['flexible', 'Flexible'],
];
const BUDGETS = [
  ['', 'Prefer not to say'],
  ['under-500', 'Under $500'],
  ['500-1000', '$500 – $1,000'],
  ['1000-2500', '$1,000 – $2,500'],
  ['2500-5000', '$2,500 – $5,000'],
  ['5000-plus', '$5,000+'],
  ['not-sure', 'Not sure yet'],
];

function Req() {
  return <abbr title="required" style={{ color: 'var(--accent-ink)', textDecoration: 'none' }}>*</abbr>;
}

function QuoteForm() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', projectType: '', timeline: '', budget: '',
    description: '', referenceLinks: '',
  });
  const [files, setFiles] = useState([]);
  const [state, setState] = useState({ status: 'idle', error: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === 'sending') return;
    const email = form.email.trim();
    const phone = form.phone.trim();
    const description = form.description.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setState({ status: 'error', error: 'Please enter a valid email address.' });
    if (!phone) return setState({ status: 'error', error: 'Please include a phone number so we can reach you.' });
    if (description.length < 10) return setState({ status: 'error', error: 'Tell us a little more about what you are looking for.' });

    setState({ status: 'sending', error: '' });
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('email', email);
      fd.append('phone', phone);
      fd.append('projectType', form.projectType);
      fd.append('description', description);
      fd.append('referenceLinks', form.referenceLinks.trim());
      fd.append('budget', form.budget);
      fd.append('timeline', form.timeline);
      files.forEach((f) => fd.append('attachments', f));
      const res = await fetch(`${API_BASE}/api/photographer-quote`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'That did not go through.');
      setState({ status: 'sent', error: '' });
    } catch (err) {
      setState({ status: 'error', error: err.message || 'That did not go through.' });
    }
  };

  if (state.status === 'sent') {
    return (
      <Card>
        <Eyebrow>Got it</Eyebrow>
        <p className="hddn-h3" style={{ marginTop: 14 }}>Request received.</p>
        <p className="hddn-lede" style={{ marginTop: 14, maxWidth: '46ch' }}>
          A confirmation is on its way to your inbox. We will come back with a quote and a couple of
          questions within one to two business days.
        </p>
        <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Btn href="/work" onClick={go('/work')} variant="line">See the work</Btn>
          <Btn href="/" onClick={go('/')} variant="line">Back to HDDN LAB</Btn>
        </div>
      </Card>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="hddn-grid hddn-grid--2" style={{ gap: '0 var(--gap)' }}>
        <label className="hddn-field">
          <span>Name</span>
          <input className="hddn-input" value={form.name} onChange={set('name')} autoComplete="name" />
        </label>
        <label className="hddn-field">
          <span>Email <Req /></span>
          <input className="hddn-input" type="email" required value={form.email} onChange={set('email')} autoComplete="email" />
        </label>
        <label className="hddn-field">
          <span>Phone <Req /></span>
          <input className="hddn-input" type="tel" required value={form.phone} onChange={set('phone')} autoComplete="tel" />
        </label>
        <label className="hddn-field">
          <span>What are we shooting?</span>
          <select className="hddn-input" value={form.projectType} onChange={set('projectType')}>
            <option value="">Select…</option>
            {PHOTOGRAPHY.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="other">Something else</option>
          </select>
        </label>
        <label className="hddn-field">
          <span>When</span>
          <select className="hddn-input" value={form.timeline} onChange={set('timeline')}>
            <option value="">Select…</option>
            {TIMELINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="hddn-field">
          <span>Budget <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></span>
          <select className="hddn-input" value={form.budget} onChange={set('budget')}>
            {BUDGETS.map(([v, l]) => <option key={v || 'none'} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <label className="hddn-field">
        <span>Tell us about it <Req /></span>
        <textarea
          className="hddn-input"
          required
          rows={6}
          value={form.description}
          onChange={set('description')}
          placeholder="Who or what is in front of the camera, where, how many finished photos you need, and what they are for."
        />
      </label>
      <label className="hddn-field">
        <span>Reference links <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></span>
        <textarea className="hddn-input" rows={2} value={form.referenceLinks} onChange={set('referenceLinks')} placeholder="Instagram posts, Pinterest boards, anything that shows the look." />
      </label>
      <label className="hddn-field">
        <span>Attachments <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional, up to 8)</span></span>
        <input
          className="hddn-input"
          type="file"
          multiple
          accept="image/*,.pdf"
          onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 8))}
        />
        {files.length ? <span className="hddn-note" style={{ display: 'block', marginTop: 6, textTransform: 'none', letterSpacing: 0 }}>{files.length} file{files.length === 1 ? '' : 's'} attached</span> : null}
      </label>

      {state.status === 'error' ? (
        <p className="hddn-alert" role="alert" style={{ marginBottom: 18, borderColor: 'var(--accent)' }}>{state.error}</p>
      ) : null}

      <Btn onClick={submit} variant="fill" disabled={state.status === 'sending'}>
        {state.status === 'sending' ? 'Sending…' : 'Request a quote'}
      </Btn>
      <p className="hddn-note" style={{ marginTop: 14 }}>
        Or just email <a href={`mailto:${STUDIO.email}`} style={{ color: 'var(--accent-ink)', textDecoration: 'underline' }}>{STUDIO.email}</a>.
      </p>
    </form>
  );
}

export default function Photographer() {
  const d = useSiteData();
  const work = workPhotos(d.photos);

  return (
    <>
      <Bay band="ground">
        <Eyebrow>{PHOTOGRAPHY.eyebrow}</Eyebrow>
        <h1 className="hddn-h1" style={{ marginTop: 18, maxWidth: '14ch' }}>{PHOTOGRAPHY.title}</h1>
        <p className="hddn-lede" style={{ marginTop: 22, maxWidth: '52ch' }}>{PHOTOGRAPHY.lede}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 30 }}>
          <Btn href="#request" variant="fill">Request a session</Btn>
          <Btn href="/work" onClick={go('/work')} variant="line">See the work</Btn>
        </div>
      </Bay>

      {work.length ? (
        <Bay tight>
          <PhotoStrip photos={work} alt="Photography by HDDN LAB" ratio="tall" limit={9} />
        </Bay>
      ) : null}

      <Bay>
        <Head eyebrow="What we shoot" title="Most things with a person or a product in them." />
        <div className="hddn-defs" style={{ maxWidth: 760 }}>
          {PHOTOGRAPHY.types.map((t) => (
            <div className="hddn-def" key={t.id}>
              <dt style={{ fontFamily: 'var(--sans)', fontSize: 'var(--step-0)', fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: 'var(--ink)' }}>{t.name}</dt>
              <dd className="hddn-muted">{t.d}</dd>
            </div>
          ))}
        </div>
      </Bay>

      <Bay band="raised">
        <Head eyebrow="How it works" title="Three steps, no menu." lede={PHOTOGRAPHY.note} />
        <div className="hddn-grid hddn-grid--3">
          {PHOTOGRAPHY.steps.map((s, i) => (
            <Card key={s.t}>
              <p className="hddn-label">Step {i + 1}</p>
              <p className="hddn-h4" style={{ marginTop: 10 }}>{s.t}</p>
              <p className="hddn-muted" style={{ marginTop: 10 }}>{s.d}</p>
            </Card>
          ))}
        </div>
      </Bay>

      <Bay id="request">
        <div className="hddn-grid hddn-grid--sidebar">
          <div>
            <Head eyebrow="Request a session" title="Tell us what you need." lede="Two minutes. The more you tell us, the closer the first quote lands." />
            <QuoteForm />
          </div>
          <Card>
            <p className="hddn-label">The studio</p>
            <address style={{ fontStyle: 'normal', marginTop: 14 }} className="hddn-h4">
              {STUDIO.street}<br />{STUDIO.city}, {STUDIO.state} {STUDIO.zip}
            </address>
            <p className="hddn-muted" style={{ marginTop: 14 }}>{STUDIO.hoursLabel}</p>
            <hr className="hddn-rule" style={{ margin: '22px 0' }} />
            <p className="hddn-note">
              Studio sessions use the full studio: lighting, modifiers, backdrops and an MUA station
              are all here. On-location sessions come to you anywhere in Westchester and the metro.
            </p>
          </Card>
        </div>
      </Bay>

      <OtherDoor
        k="The other doors"
        title="Want a month of content, or the room itself?"
        body="The same team makes video and photo for your social on a monthly retainer. And the studio rents by the hour and the day if you would rather shoot it yourself."
        href="/content"
        cta="See content creation"
      />
    </>
  );
}
